import { useEffect, useState, useRef } from 'react';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  tipsAtom,
  filteredTipsAtom,
  tipsFilterAtom,
  tipsSearchQueryAtom,
  tipsLoadingAtom,
  tipsErrorAtom,
  fetchTipsActionAtom,
  approveTipActionAtom,
  rejectTipActionAtom,
  deleteTipActionAtom,
  triggerImapIngestActionAtom,
} from '@/stores/tipsStore';
import { CreateAssignmentDialog } from '@/components/CreateAssignmentDialog';

import { TipsHeader } from '@/components/tip/TipsHeader';
import { TipsSearch } from '@/components/tip/TipsSearch';
import { TipsStatusAlert } from '@/components/tip/TipsStatusAlert';
import { TipsList } from '@/components/tip/TipsList';
import { TipResponse } from '@/types/tips';

export function TipInbox() {
  const navigate = useNavigate();
  const tips = useAtomValue(filteredTipsAtom);
  const allTips = useAtomValue(tipsAtom);
  const [filter, setFilter] = useAtom(tipsFilterAtom);
  const [searchQuery, setSearchQuery] = useAtom(tipsSearchQueryAtom);
  const isLoading = useAtomValue(tipsLoadingAtom);
  const error = useAtomValue(tipsErrorAtom);

  const [selectedTipForAssignment, setSelectedTipForAssignment] =
    useState<TipResponse | null>(null);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const hasFetchedRef = useRef(false);

  const fetchTips = useSetAtom(fetchTipsActionAtom);
  const approveAction = useSetAtom(approveTipActionAtom);
  const rejectAction = useSetAtom(rejectTipActionAtom);
  const deleteTipAction = useSetAtom(deleteTipActionAtom);
  const triggerImapIngest = useSetAtom(triggerImapIngestActionAtom);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchTips().catch(console.error);
  }, [fetchTips]);

  const handleRefresh = () => {
    fetchTips().catch(console.error);
  };

  const handleApprove = async (tipId: string) => {
    await approveAction(tipId);
  };

  const handleReject = async (tipId: string) => {
    await rejectAction(tipId);
  };

  const handleCreateAssignment = (tip: TipResponse) => {
    setSelectedTipForAssignment(tip);
    setIsAssignmentDialogOpen(true);
  };

  const handleEditTip = (tip: TipResponse) => {
    navigate(`/inbox/${tip.id}/edit`);
  };

  const handleDeleteTip = async (tipId: string) => {
    if (!confirm('Are you sure you want to delete this tip?')) return;
    await deleteTipAction(tipId);
  };

  const handleNewTip = () => {
    navigate('/inbox/new');
  };

  const handleImapIngest = async () => {
    await triggerImapIngest();
  };

  const handleToggleMockMode = () => {
    toast.info('Mock mode toggle not available');
  };

  const isMockMode = false;

  return (
    <div className='h-full flex flex-col overflow-hidden'>
      <div className='p-4 border-b border-border bg-background shrink-0'>
        <TipsHeader
          isLoading={isLoading}
          onRefresh={handleRefresh}
          onNewTip={handleNewTip}
          onImapIngest={handleImapIngest}
          isMockMode={isMockMode}
          onToggleMockMode={handleToggleMockMode}
          totalTips={allTips.length}
          shownTips={tips.length}
        />

        <TipsSearch
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filter={filter}
          onFilterChange={setFilter}
        />

        <TipsStatusAlert
          error={error}
          isMockMode={isMockMode}
          onRefresh={handleRefresh}
        />
      </div>

      {/* Scrollable Content Area */}
      <div className='flex-1 overflow-hidden'>
        <TipsList
          tips={tips}
          isLoading={isLoading}
          onApprove={handleApprove}
          onReject={handleReject}
          onCreateAssignment={handleCreateAssignment}
          onEdit={handleEditTip}
          onDelete={handleDeleteTip}
        />
      </div>

      {/* Create Assignment Dialog */}
      {selectedTipForAssignment && (
        <CreateAssignmentDialog
          tip={selectedTipForAssignment}
          open={isAssignmentDialogOpen}
          onOpenChange={setIsAssignmentDialogOpen}
        />
      )}
    </div>
  );
}
